import { captureException } from '@sentry/react';
import { useCallback, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { AiChatReplyStatus } from '../../../server/utils/ai-chat-reply-status.ts';
import { useAppRegistry } from '../../../use-app-registry.ts';
import { api, type RouterOutputs } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import type { AiChatCopiedVia, AiChatNewChatReason } from '../../analytics/types/analytics-events.ts';
import {
    countPageLinks,
    hasPathParams,
    maskPathIds,
    type PageLinkClick,
} from '../components/reply-markdown.tsx';
import { pressMessageRating, type AskPlatoMessageRating } from '../types/message-rating.ts';
import { conversationRecencyGroup } from '../utils/conversation-recency.ts';

export type { AiChatReplyStatus, AskPlatoMessageRating };

/**
 * One rendered slice of a message: text parts become bubbles, tool-call parts
 * become caption rows between them. Mirrors the persisted
 * `PrismaJson.AiChatMessagePart` minus the fields the UI has no use for.
 */
export type AskPlatoMessagePart =
    | { type: 'text'; text: string }
    | {
          type: 'toolCall';
          id: string;
          /** Tool name; the caption is looked up by it (hooks/use-tool-caption.ts). */
          name: string;
          status: 'running' | 'done' | 'failed' | 'interrupted';
          /** The search term the tool ran with (the model's argument), quoted in the caption. */
          search?: string;
      };

type ReplayedMessage = RouterOutputs['platoAiChat']['conversations']['getMessages']['messages'][number];

/**
 * A persisted part as the UI renders it. Persisted tool calls are always
 * settled; parts stored before GROW-365 carry `failed` instead of `outcome`.
 */
function replayedPart(part: ReplayedMessage['parts'][number]): AskPlatoMessagePart {
    if (part.type === 'text') {
        return part;
    }
    return {
        type: 'toolCall',
        id: part.id,
        name: part.name,
        status: part.outcome ?? (part.failed === true ? 'failed' : 'done'),
        search: part.search,
    };
}

export type AskPlatoChatMessage = {
    id: string;
    role: 'user' | 'assistant';
    /** Ordered content — text bubbles alternating with tool-call captions. */
    parts: AskPlatoMessagePart[];
    messageIndex: number;
    /**
     * The `ai__chat_message` id the server assigned, known from the terminal
     * chunk on. A replayed message carries it as `id` already.
     */
    persistedId?: string;
    rating?: AskPlatoMessageRating;
    /**
     * The outcome the server assigned this reply, set from the terminal chunk.
     * Absent while streaming, on replies whose stream died before the server
     * could report on it (those surface through `hasError` instead), and on
     * messages replayed from a resumed conversation.
     */
    status?: AiChatReplyStatus;
};

/** The message's last text part — the bubble the action row belongs to. */
export function lastTextPart(message: AskPlatoChatMessage): { type: 'text'; text: string } | undefined {
    return message.parts.findLast((part) => part.type === 'text');
}

/** The message's text parts joined — for analytics and the support prefill. */
export function messagePlainText(message: AskPlatoChatMessage): string {
    return message.parts
        .filter((part) => part.type === 'text')
        .map((part) => part.text)
        .join('\n\n');
}

/** One row of the history view. */
export type AskPlatoConversation = RouterOutputs['platoAiChat']['conversations']['getMany'][number];

export type AskPlatoChat = {
    messages: AskPlatoChatMessage[];
    isStreaming: boolean;
    /** A past conversation is being fetched and will replace the transcript when it arrives. */
    isLoading: boolean;
    hasError: boolean;
    /** The conversation's generated title — the header's topic caption. */
    title: string | undefined;
    /**
     * The conversation is closed because its latest reply sent the LLM at
     * least the server's `aiChatMaxContextTokens`. No further questions are
     * accepted.
     */
    contextLimitReached: boolean;
    /** Past conversations, most recent first — `undefined` until loaded. */
    conversations: AskPlatoConversation[] | undefined;
    sendMessage: (message: string) => void;
    toggleRating: (messageId: string, rating: AskPlatoMessageRating) => void;
    /** Drops the failed turn and sends its question again. */
    retry: () => void;
    /**
     * Clears the transcript and starts the next message in a fresh conversation.
     * No-op while empty, and while a reply streams or a past conversation loads
     * unless `interrupt` is set: then that reply or load is dropped from view, and
     * a reply stays with its own conversation.
     */
    startNewChat: (reason: AiChatNewChatReason, options?: { interrupt?: boolean }) => void;
    /** Replaces the transcript with a past conversation's persisted messages. */
    resumeConversation: (input: {
        conversationId: string;
        title: string | undefined;
        updatedAt: Date;
    }) => void;
    /** Reports a copy of `message`'s text; the clipboard write itself lives in the UI. */
    reportCopied: (message: AskPlatoChatMessage, via: AiChatCopiedVia) => void;
    reportPageLinkClicked: (message: AskPlatoChatMessage, click: PageLinkClick) => void;
};

type UseAskPlatoChatOptions = {
    /** History is only fetched while the chat is open. */
    open: boolean;
};

export function useAskPlatoChat({ open }: UseAskPlatoChatOptions): AskPlatoChat {
    const sendEvent = useAnalytics();
    const [messages, setMessages] = useState<AskPlatoChatMessage[]>([]);
    const [isStreaming, setIsStreaming] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [title, setTitle] = useState<string | undefined>(undefined);
    const [contextLimitReached, setContextLimitReached] = useState(false);
    const conversationIdRef = useRef<string | undefined>(undefined);
    const messageIndexRef = useRef(0);
    // Refs mirror `isStreaming` and `isLoading` so the callbacks below can
    // check them synchronously, before React has rendered the state update.
    const streamingRef = useRef(false);
    const loadingRef = useRef(false);
    // Bumped when a new chat interrupts a streaming reply or a history load;
    // a stream or load whose generation is stale stops writing into the transcript.
    const generationRef = useRef(0);
    const contextLimitReachedRef = useRef(false);
    // Mirrors `messages.length` so `startNewChat` keeps one identity across
    // messages; the provider wires it into the global shortcut handler.
    const messageCountRef = useRef(0);
    messageCountRef.current = messages.length;
    const apiUtils = api.useUtils();

    const appRegistry = useAppRegistry();

    const { mutateAsync: createConversation } = api.platoAiChat.conversations.create.useMutation();
    const { mutateAsync: sendChatMessage } = api.platoAiChat.conversations.sendMessage.useMutation();
    const { mutateAsync: generateTitle } = api.platoAiChat.conversations.generateTitle.useMutation();
    const { data: conversations } = api.platoAiChat.conversations.getMany.useQuery({}, { enabled: open });
    const invalidateConversations = useCallback(() => {
        void apiUtils.platoAiChat.conversations.getMany.invalidate();
    }, [apiUtils]);

    const send = useCallback(
        (message: string, { retry: isRetry }: { retry: boolean }) => {
            const trimmedMessage = message.trim();
            if (trimmedMessage.length === 0 || streamingRef.current || loadingRef.current) {
                return;
            }
            if (contextLimitReachedRef.current && !isRetry) {
                return;
            }

            streamingRef.current = true;
            setIsStreaming(true);
            setHasError(false);
            const generation = generationRef.current;
            const isCurrent = (): boolean => generationRef.current === generation;

            const messageIndex = messageIndexRef.current;
            messageIndexRef.current += 1;
            sendEvent('ai_chat_message_submitted', {
                conversation_id: conversationIdRef.current,
                message_index: messageIndex,
                is_retry: isRetry,
                message_length_chars: trimmedMessage.length,
                message_text: trimmedMessage,
            });

            const assistantMessageId = uuidv4();
            // The reply text as streamed, for the link count on the terminal chunk.
            let assistantText = '';
            setMessages((previous) => [
                ...previous,
                {
                    id: uuidv4(),
                    role: 'user',
                    parts: [{ type: 'text', text: trimmedMessage }],
                    messageIndex,
                },
                { id: assistantMessageId, role: 'assistant', parts: [], messageIndex },
            ]);

            const appendToAssistant = (
                update: (parts: AskPlatoMessagePart[]) => AskPlatoMessagePart[],
            ): void => {
                setMessages((previous) =>
                    previous.map((item) =>
                        item.id === assistantMessageId ? { ...item, parts: update(item.parts) } : item,
                    ),
                );
            };

            void (async () => {
                try {
                    let conversationId = conversationIdRef.current;
                    const isFirstMessage = conversationId === undefined;

                    if (conversationId === undefined) {
                        ({ conversationId } = await createConversation());
                        if (!isCurrent()) {
                            return;
                        }
                        conversationIdRef.current = conversationId;
                    }

                    // The title is derived from the opening message only, and the
                    // reply must not wait on it — the server persists it itself, so
                    // fire it alongside the stream and let a failure stay silent.
                    if (isFirstMessage) {
                        const titleConversationId = conversationId;
                        void (async () => {
                            try {
                                const { title: generated } = await generateTitle({
                                    conversationId: titleConversationId,
                                    message: trimmedMessage,
                                });
                                if (isCurrent()) {
                                    setTitle(generated);
                                }
                                invalidateConversations();
                            } catch (error) {
                                captureException(error);
                            }
                        })();
                    }

                    const chunks = await sendChatMessage({
                        conversationId,
                        message: trimmedMessage,
                        retry: isRetry,
                        appRegistry,
                    });

                    for await (const chunk of chunks) {
                        if (!isCurrent()) {
                            break;
                        }
                        if (chunk.type === 'delta') {
                            assistantText += chunk.text;
                            // Text flows into the current bubble; after a tool call
                            // (or at the reply's start) a new bubble opens.
                            appendToAssistant((parts) => {
                                const last = parts.at(-1);
                                if (last?.type === 'text') {
                                    return [
                                        ...parts.slice(0, -1),
                                        { type: 'text', text: last.text + chunk.text },
                                    ];
                                }
                                return [...parts, { type: 'text', text: chunk.text }];
                            });
                        }
                        // A tool invocation starts as its own caption row.
                        if (chunk.type === 'toolCall') {
                            appendToAssistant((parts) => [
                                ...parts,
                                { type: 'toolCall', id: chunk.id, name: chunk.name, status: 'running' },
                            ]);
                        }
                        // The outcome replaces the running state in place.
                        if (chunk.type === 'toolStatus') {
                            appendToAssistant((parts) =>
                                parts.map((part) =>
                                    part.type === 'toolCall' && part.id === chunk.id
                                        ? { ...part, status: chunk.outcome, search: chunk.search }
                                        : part,
                                ),
                            );
                        }
                        // The terminal chunk. On the server's error path it is
                        // flushed before the throw, so the outcome is reported for
                        // failed replies too — the loop rejects on the next pull.
                        if (chunk.type === 'done') {
                            const { status } = chunk;
                            setMessages((previous) =>
                                previous.map((item) =>
                                    item.id === assistantMessageId
                                        ? { ...item, status, persistedId: chunk.messageId }
                                        : item,
                                ),
                            );
                            sendEvent('ai_chat_reply_shown', {
                                status: chunk.status,
                                conversation_id: chunk.conversationId,
                                message_index: messageIndex,
                                message_id: chunk.messageId,
                                link_count: countPageLinks(assistantText),
                            });
                            contextLimitReachedRef.current = chunk.contextLimitReached;
                            setContextLimitReached(chunk.contextLimitReached);
                            if (chunk.contextLimitReached) {
                                sendEvent('ai_chat_context_limit_reached', {
                                    conversation_id: chunk.conversationId,
                                    question_count: messageIndex + 1,
                                });
                            }
                        }
                    }
                } catch (error) {
                    captureException(error);
                    if (!isCurrent()) {
                        return;
                    }
                    setHasError(true);
                    setMessages((previous) =>
                        previous
                            .map((item) =>
                                item.id === assistantMessageId
                                    ? {
                                          ...item,
                                          // A call that never settled reads the same as it does on replay.
                                          parts: item.parts.map((part) =>
                                              part.type === 'toolCall' && part.status === 'running'
                                                  ? { ...part, status: 'interrupted' as const }
                                                  : part,
                                          ),
                                      }
                                    : item,
                            )
                            .filter(
                                (item) => item.id !== assistantMessageId || messagePlainText(item).length > 0,
                            ),
                    );
                } finally {
                    if (isCurrent()) {
                        streamingRef.current = false;
                        setIsStreaming(false);
                    }
                    // The conversation is new or its `updatedAt` moved.
                    invalidateConversations();
                }
            })();
        },
        [createConversation, sendChatMessage, generateTitle, sendEvent, invalidateConversations, appRegistry],
    );

    const sendMessage = useCallback(
        (message: string) => {
            send(message, { retry: false });
        },
        [send],
    );

    const toggleRating = useCallback(
        (messageId: string, rating: AskPlatoMessageRating) => {
            const message = messages.find((item) => item.id === messageId);
            if (message === undefined || message.role !== 'assistant') {
                return;
            }

            const press = pressMessageRating(message.rating, rating);

            setMessages((previous) =>
                previous.map((item) => (item.id === messageId ? { ...item, rating: press.rating } : item)),
            );

            sendEvent('ai_chat_message_rated', {
                conversation_id: conversationIdRef.current,
                message_index: message.messageIndex,
                rating: press.reported,
                response_length_chars: messagePlainText(message).length,
            });
        },
        [messages, sendEvent],
    );

    const retry = useCallback(() => {
        if (streamingRef.current || loadingRef.current) {
            return;
        }
        const questionIndex = messages.findLastIndex((item) => item.role === 'user');
        const question = messages[questionIndex];
        if (question === undefined) {
            return;
        }
        // The failed turn is replaced, not appended to, so its index is reused.
        setMessages(messages.slice(0, questionIndex));
        messageIndexRef.current = question.messageIndex;
        setHasError(false);
        send(messagePlainText(question), { retry: true });
    }, [messages, send]);

    const startNewChat = useCallback(
        (reason: AiChatNewChatReason, options?: { interrupt?: boolean }) => {
            if (streamingRef.current || loadingRef.current) {
                if (options?.interrupt !== true) {
                    return;
                }
                generationRef.current += 1;
                streamingRef.current = false;
                loadingRef.current = false;
                setIsStreaming(false);
                setIsLoading(false);
            }
            // Nothing to clear on an empty transcript, and nothing to report.
            if (messageCountRef.current === 0) {
                return;
            }
            sendEvent('ai_chat_new_chat_started', {
                previous_conversation_id: conversationIdRef.current,
                previous_message_count: messageCountRef.current,
                reason,
            });
            conversationIdRef.current = undefined;
            messageIndexRef.current = 0;
            contextLimitReachedRef.current = false;
            setMessages([]);
            setTitle(undefined);
            setHasError(false);
            setContextLimitReached(false);
        },
        [sendEvent],
    );

    const resumeConversation = useCallback(
        ({
            conversationId,
            title: nextTitle,
            updatedAt,
        }: {
            conversationId: string;
            title: string | undefined;
            updatedAt: Date;
        }) => {
            if (streamingRef.current || loadingRef.current) {
                return;
            }

            // Block sending and switching until the fetch resolves. Anything sent
            // in the meantime would be overwritten when the transcript arrives.
            loadingRef.current = true;
            setIsLoading(true);
            const generation = generationRef.current;
            const isCurrent = (): boolean => generationRef.current === generation;

            void (async () => {
                try {
                    const result = await apiUtils.platoAiChat.conversations.getMessages.fetch({
                        conversationId,
                    });
                    if (!isCurrent()) {
                        return;
                    }

                    let messageIndex = -1;
                    const replayed: AskPlatoChatMessage[] = [];
                    for (const message of result.messages) {
                        if (message.role !== 'user' && message.role !== 'assistant') {
                            continue;
                        }
                        if (message.role === 'user') {
                            messageIndex += 1;
                        }
                        replayed.push({
                            id: message.id,
                            role: message.role,
                            parts: message.parts.map(replayedPart),
                            // Ratings are client state only, so they reset on resume.
                            messageIndex: Math.max(messageIndex, 0),
                        });
                    }

                    conversationIdRef.current = conversationId;
                    messageIndexRef.current = messageIndex + 1;
                    contextLimitReachedRef.current = result.contextLimitReached;
                    setMessages(replayed);
                    setTitle(result.title ?? nextTitle);
                    setHasError(false);
                    setContextLimitReached(result.contextLimitReached);
                    sendEvent('ai_chat_conversation_resumed', {
                        conversation_id: conversationId,
                        message_count: replayed.length,
                        recency: conversationRecencyGroup(updatedAt, new Date()),
                    });
                } catch (error) {
                    captureException(error);
                    if (isCurrent()) {
                        setHasError(true);
                    }
                } finally {
                    if (isCurrent()) {
                        loadingRef.current = false;
                        setIsLoading(false);
                    }
                }
            })();
        },
        [apiUtils, sendEvent],
    );

    const reportCopied = useCallback(
        (message: AskPlatoChatMessage, via: AiChatCopiedVia) => {
            sendEvent('ai_chat_message_copied', {
                conversation_id: conversationIdRef.current,
                message_index: message.messageIndex,
                role: message.role,
                via,
            });
        },
        [sendEvent],
    );

    const reportPageLinkClicked = useCallback(
        (message: AskPlatoChatMessage, { path, newTab }: PageLinkClick) => {
            sendEvent('ai_chat_link_clicked', {
                conversation_id: conversationIdRef.current,
                message_index: message.messageIndex,
                message_id: message.persistedId ?? message.id,
                path: maskPathIds(path),
                has_params: hasPathParams(path),
                new_tab: newTab,
            });
        },
        [sendEvent],
    );

    return {
        messages,
        isStreaming,
        isLoading,
        hasError,
        title,
        contextLimitReached,
        conversations,
        sendMessage,
        toggleRating,
        retry,
        startNewChat,
        resumeConversation,
        reportCopied,
        reportPageLinkClicked,
    };
}
